import { isApplicationAdmin, isSuperUser } from '@/util'
import { Menu, MenuTrigger, Button, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components'
import { Navigation24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { RestSchemas } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { MaintainSupportTeam } from '@/ui/ApplicationControl/MaintainSupportTeam'
import { MaintainSupportTeamRoles } from '@/ui/ApplicationControl/MaintainSupportTeamRoles'
import { ApplicationTeamForm } from '@/ui/Forms/ApplicationTeamForm'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'

type MessagesApplicationsApplicationTeam = RestSchemas['Messages.Applications.ApplicationTeam']

const permissions = (userRole: number) => {
  return {
    canAddTeamMembers: isSuperUser(userRole) || isApplicationAdmin(userRole),
    canMaintainSupportTeamRoles: isSuperUser(userRole) || isApplicationAdmin(userRole),
  }
}

export function RowActionsMenu({ appTeam }: { appTeam: MessagesApplicationsApplicationTeam }) {
  const [userRole] = useSessionStorage('roles', 1)
  const [drawerOpen, setDrawerOpen] = useState<'edit' | 'maintainTeam' | 'maintainSupportTeamRoles' | 'none'>('none')
  const { canAddTeamMembers, canMaintainSupportTeamRoles } = permissions(userRole)
  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<Navigation24Regular />} />
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            <MenuItem
              onClick={() => {
                setDrawerOpen('edit')
              }}
            >
              Edit team
            </MenuItem>
            {canAddTeamMembers && (
              <MenuItem
                onClick={() => {
                  setDrawerOpen('maintainTeam')
                }}
              >
                Maintain support team members
              </MenuItem>
            )}
            {canMaintainSupportTeamRoles && (
              <MenuItem
                onClick={() => {
                  setDrawerOpen('maintainSupportTeamRoles')
                }}
              >
                Maintain support team roles
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>

      {appTeam.applicationId && (
        <OverlayDrawerComponent
          key="editform"
          open={drawerOpen === 'edit'}
          size="medium"
          setOpen={(open) => setDrawerOpen(open ? 'edit' : 'none')}
          title={'Edit support team'}
          content={
            <ApplicationTeamForm
              onSubmit={() => setDrawerOpen('none')}
              selectedTeam={appTeam}
              applicationId={appTeam.applicationId}
            />
          }
        />
      )}

      <OverlayDrawerComponent
        key="maintainTeam"
        open={drawerOpen === 'maintainTeam'}
        setOpen={(open) => setDrawerOpen(open ? 'maintainTeam' : 'none')}
        size="medium"
        title={'Maintain support team members'}
        content={<MaintainSupportTeam selectedSupportTeam={appTeam} />}
      />

      <OverlayDrawerComponent
        key="maintainSupportTeamRoles"
        open={drawerOpen === 'maintainSupportTeamRoles'}
        setOpen={(open) => setDrawerOpen(open ? 'maintainSupportTeamRoles' : 'none')}
        size="medium"
        title={'Maintain support team roles'}
        content={
          <MaintainSupportTeamRoles
            orgTeamUser={{
              id: appTeam.id,
              givenName: '',
              familyName: '',
              email: '',
              active: false,
              isFrozen: false,
              teamCount: 0,
              directLinkCount: 0,
              roles: 0,
              profilePicture: 'Default Profile Pic',
              userId: 0,
              teamId: appTeam.id,
              teamName: appTeam.name ?? '', // Add missing properties
              linkedProjectCount: appTeam.linkedProjectCount ?? 0, // Add missing properties
              activeLinkedProjectCount: appTeam.activeLinkedProjectCount ?? 0, // Add missing properties
              linkedRoleCount: appTeam.linkedRoleCount ?? 0,
              organisationId: 0,
              application: appTeam.application ?? '',
              applicationId: appTeam.applicationId ?? 0,
              organisation: '',
              typeName: appTeam.typeName?.toString() ?? '',
            }}
          />
        }
      />
    </>
  )
}
